import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAttributes, schema, supabase } from '@radiology/api-client';

/**
 * Devices data access.
 *
 * Reads go through `devices.device_inventory`, not the table. That view is
 * `security_invoker`, so RLS still applies — but it also computes
 * `years_in_service` on every read instead of returning the stored value, which
 * the legacy app recalculated only when somebody opened the edit form. 69 of the
 * 154 migrated devices carry a wrong stored figure right now.
 *
 * Writes go to the table, because a view with subqueries is not updatable.
 */

export type DueStatus = 'Up to Date' | 'Due Soon' | 'Overdue' | 'Not scheduled';

export interface Device {
  id: number;
  equipment_name: string;
  htm: string | null;
  kn: string | null;
  model: string | null;
  serial_no: string | null;
  md: string | null;
  manufacturer: string | null;
  po_no: string | null;
  site_name: string | null;
  location: string | null;
  section: string | null;
  capital_equity_information: string | null;
  color_category: string | null;
  installation_date: string | null;
  /** Computed by the view. There is no stored equivalent worth reading. */
  years_in_service: number | null;
  is_obsolete: boolean;
  is_replaced: boolean;
  refer_id: string | null;
  refer_equipment_name: string | null;
  selected_file: string | null;
  created_at: string;
  updated_at: string;

  // QC & PM tracking. The two statuses are COMPUTED by the view on every read
  // (ST-BR-03 / AC-12) — there is no stored equivalent, deliberately.
  qc_performed_by: string | null;
  last_qc_date: string | null;
  next_qc_date: string | null;
  qc_status: DueStatus;
  pm_performed_by: string | null;
  last_pm_date: string | null;
  next_pm_date: string | null;
  pm_status: DueStatus;

  maintenance_count: number;
  downtime_count: number;
  downtime_minutes: number;
  trained_count: number;
}

const KEY = ['devices', 'inventory'] as const;

/** PostgREST truncates at 1,000 rows without saying so. 154 today, but it grows. */
const PAGE = 1000;

export interface DeviceFilters {
  qcStatus?: DueStatus;
  pmStatus?: DueStatus;
}

export function useDevices(filter?: 'all' | 'obsolete' | 'active', extra?: DeviceFilters) {
  return useQuery({
    queryKey: [...KEY, filter ?? 'all', extra?.qcStatus ?? '', extra?.pmStatus ?? ''],
    queryFn: async (): Promise<Device[]> => {
      const all: Device[] = [];

      for (let from = 0; ; from += PAGE) {
        let query = schema('devices')
          .from('device_inventory')
          .select('*')
          .order('id', { ascending: false })
          .range(from, from + PAGE - 1);

        if (filter === 'obsolete') query = query.eq('is_obsolete', true);
        if (filter === 'active') query = query.eq('is_obsolete', false);
        // ST-BR-05 / AC-14 — filtering happens in the database, on the computed
        // status, so it is consistent with what the column shows.
        if (extra?.qcStatus) query = query.eq('qc_status', extra.qcStatus);
        if (extra?.pmStatus) query = query.eq('pm_status', extra.pmStatus);

        const { data, error } = await query.returns<Device[]>();
        if (error) throw new Error(error.message);

        all.push(...(data ?? []));
        if (!data || data.length < PAGE) break;
      }

      return all;
    },
  });
}

export interface DeviceSummaryRow {
  site: string;
  section: string;
  total: number;
  obsolete: number;
  added_last_year: number;
  average_age: number | null;
}

/**
 * The manager dashboard's numbers.
 *
 * Replaces `getManagementInfo()`, which fetched every device into Node and
 * counted them in JavaScript.
 */
export function useDeviceSummary() {
  return useQuery({
    queryKey: ['devices', 'summary'],
    queryFn: async (): Promise<DeviceSummaryRow[]> => {
      const { data, error } = await schema('devices')
        .from('device_summary')
        .select('*')
        .returns<DeviceSummaryRow[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export interface DeviceInput {
  equipment_name: string;
  model?: string;
  serial_no?: string;
  manufacturer?: string;
  htm?: string;
  kn?: string;
  md?: string;
  po_no?: string;
  site_name?: string;
  location?: string;
  section?: string;
  capital_equity_information?: string;
  color_category?: string;
  installation_date?: string;
  obsolete?: 'Yes' | 'No';
  qc_performed_by?: string;
  last_qc_date?: string;
  next_qc_date?: string;
  pm_performed_by?: string;
  last_pm_date?: string;
  next_pm_date?: string;
}

/**
 * `obsolete` is still the legacy text column; `is_obsolete` is generated from it
 * and cannot be written. Writing the text and reading the boolean is deliberate —
 * it is what let the 154 rows load as a plain COPY.
 */
export function useSaveDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: DeviceInput & { id?: number }) => {
      const { id, installation_date, ...rest } = input;
      const row = {
        ...rest,
        obsolete: input.obsolete ?? 'No',
        installation_date: installation_date || null,
        // Empty means "not set", not "1970". The trigger fills a blank next date
        // from last + 1 year; sending '' would be a cast error.
        last_qc_date: input.last_qc_date || null,
        next_qc_date: input.next_qc_date || null,
        last_pm_date: input.last_pm_date || null,
        next_pm_date: input.next_pm_date || null,
      };

      const table = schema('devices').from('devices');
      const { error } = id
        ? await table.update(row).eq('id', id)
        : await table.insert({ ...row, replaced: 'false' });

      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
      void queryClient.invalidateQueries({ queryKey: ['devices', 'summary'] });
    },
  });
}

/**
 * Deleting a device cascades away its maintenance history, downtime record and
 * trained-employee list, so the policy allows it only for admins. A secretary
 * retires a device by marking it obsolete.
 */
export function useDeleteDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { error } = await schema('devices').from('devices').delete().eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: KEY });
      void queryClient.invalidateQueries({ queryKey: ['devices', 'summary'] });
    },
  });
}

export interface MaintenanceRow {
  id: number;
  device_id: number;
  status: string | null;
  date: string | null;
  created_at: string;
}

export interface DowntimeRow {
  id: number;
  device_id: number;
  component_name: string | null;
  duration: number | null;
  start_moment: string | null;
  end_moment: string | null;
}

export interface TrainedRow {
  id: number;
  device_id: number;
  employee_name: string;
}

/** Everything hanging off one device, for the detail dialog. */
export function useDeviceChildren(deviceId: number | null) {
  return useQuery({
    queryKey: ['devices', 'children', deviceId],
    enabled: deviceId !== null,
    queryFn: async () => {
      const db = schema('devices');
      const [maintenance, downtime, trained] = await Promise.all([
        db
          .from('maintenance_components')
          .select('*')
          .eq('device_id', deviceId!)
          .order('id', { ascending: false })
          .returns<MaintenanceRow[]>(),
        db
          .from('downtime_components')
          .select('*')
          .eq('device_id', deviceId!)
          .order('id', { ascending: false })
          .returns<DowntimeRow[]>(),
        db.from('trained_employees').select('*').eq('device_id', deviceId!).returns<TrainedRow[]>(),
      ]);

      const failed = [maintenance, downtime, trained].find(r => r.error);
      if (failed?.error) throw new Error(failed.error.message);

      return {
        maintenance: maintenance.data ?? [],
        downtime: downtime.data ?? [],
        trained: trained.data ?? [],
      };
    },
  });
}

export function useAddMaintenance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { deviceId: number; status: string; date: string }) => {
      const { error } = await schema('devices').from('maintenance_components').insert({
        device_id: input.deviceId,
        status: input.status,
        date: input.date,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: (_r, input) => {
      void queryClient.invalidateQueries({ queryKey: ['devices', 'children', input.deviceId] });
      void queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
}

/** Dropdown values, from `core.attributes` — the old Settings singleton. */
export function useDeviceAttributes(key: string) {
  return useQuery({
    queryKey: ['devices', 'attributes', key],
    queryFn: () => fetchAttributes('devices', key),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * A short-lived signed URL for a device document.
 *
 * The bucket is private and storage RLS derives access from the device row. The
 * legacy route — `GET /api/device/file/:filename` — was registered without the
 * token middleware while every sibling route had it, so anyone who guessed a
 * filename got the file without signing in.
 */
export async function getDocumentUrl(path: string): Promise<string> {
  const { data, error } = await supabase()
    .storage.from('device-documents')
    .createSignedUrl(path, 120);

  if (error || !data) throw new Error('That document could not be opened.');
  return data.signedUrl;
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: { deviceId: number; file: File }) => {
      // Legacy naming was `Date.now() + '-' + originalname`, which is guessable
      // by design. Scoping by device id keeps the object tied to its row.
      const safe = input.file.name.replace(/[^\w.-]+/g, '_');
      const path = `${input.deviceId}/${crypto.randomUUID()}-${safe}`;

      const { error: uploadError } = await supabase()
        .storage.from('device-documents')
        .upload(path, input.file, { upsert: false });
      if (uploadError) throw new Error(uploadError.message);

      const { error } = await schema('devices')
        .from('devices')
        .update({ selected_file: path })
        .eq('id', input.deviceId);
      if (error) throw new Error(error.message);

      return path;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Section 5 — the visual language for a due status (ST-BR-04 / AC-13). */
export const DUE_TONE: Record<DueStatus, 'success' | 'warning' | 'error' | 'default'> = {
  'Up to Date': 'success',
  'Due Soon': 'warning',
  Overdue: 'error',
  'Not scheduled': 'default',
};

export const DUE_STATUSES: DueStatus[] = ['Up to Date', 'Due Soon', 'Overdue', 'Not scheduled'];

export interface AppNotification {
  id: string;
  module: string;
  title: string;
  body: string | null;
  link: string | null;
  read_at: string | null;
  created_at: string;
}

/**
 * In-system notifications (section 6.2).
 *
 * RLS returns only the caller's own, so there is no user filter here.
 */
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async (): Promise<AppNotification[]> => {
      const { data, error } = await supabase()
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50)
        .returns<AppNotification[]>();
      if (error) throw new Error(error.message);
      return data ?? [];
    },
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase()
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

/** R / G / O in the data — the legacy UI's traffic-light condition flag. */
export const COLOR_TONE: Record<string, 'success' | 'warning' | 'error'> = {
  G: 'success',
  O: 'warning',
  R: 'error',
};

export const COLOR_LABEL: Record<string, string> = {
  G: 'Good',
  O: 'Attention',
  R: 'Critical',
};
